import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SaveIcon from '@mui/icons-material/Save'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { getComboStoreIds, parseDeliveryDays } from '../comboStores'

const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const
const DAY_KEY_TO_NUM: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}
type DayKey = typeof DAY_KEYS[number]

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const DAY_LABELS: Record<number, string> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
}

interface Schedule {
  scheduleId: number
  scheduleName: string
  scheduleType: string
  startDate: string
  endDate: string | null
  status: string
  storeIds: number[]
  storeCount: number
  combinationCount: number
}

interface ScheduleDetail {
  scheduleId: number
  scheduleName: string
  scheduleType: string
  startDate: string
  endDate: string | null
  status: string
  storeIds: number[]
  combinationId: number | null
  deliveryDays: number[] | null
  deliveryItems: string[] | null
  scheduleData: any[] | null
  combinationSource: string | null
  storesOnProvisionalGrid: number | null
  warnings?: Array<{
    prep_item_id: string
    rule: string
    reason: string
  }> | null
}

interface Props {
  schedules: Schedule[]
  locations: Array<{
    locationId: number
    locationCode: string
    locationName: string
    deliveryDays?: string
  }>
  allLocations: Array<{ id: number; name: string; code: string }>
  allPrepItems: Array<{ id: string; name: string }>
  globalSafeItems: Set<string>
  onRefresh: () => void
}

const arrayEqual = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false
  return true
}

const DAY_NUM_TO_KEY: Record<number, DayKey> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
}

const ScheduleDashboard: React.FC<Props> = ({
  schedules,
  locations,
  allLocations,
  allPrepItems,
  globalSafeItems,
  onRefresh,
}) => {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date()
    return { year: d.getFullYear(), month: d.getMonth() }
  })
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(
    null,
  )
  const [scheduleDetail, setScheduleDetail] = useState<ScheduleDetail[] | null>(
    null,
  )
  const [subTab, setSubTab] = useState(0) // 0=regular, 1=event, 2=unassigned
  const [expandedComboId, setExpandedComboId] = useState<number | null>(null)
  const [editingComboId, setEditingComboId] = useState<number | null>(null)
  const [editingGrid, setEditingGrid] = useState<any[] | null>(null)
  const [savingGrid, setSavingGrid] = useState(false)
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null)
  const [storeScheduleData, setStoreScheduleData] = useState<any[] | null>(null)
  const [storeScheduleLoading, setStoreScheduleLoading] = useState(false)
  const [editingStore, setEditingStore] = useState(false)
  const [editingStoreGrid, setEditingStoreGrid] = useState<any[] | null>(null)
  const [savingStoreGrid, setSavingStoreGrid] = useState(false)
  const [editingStoreDeliveryDays, setEditingStoreDeliveryDays] =
    useState<Set<number> | null>(null)
  const [editingStoreSafeItems, setEditingStoreSafeItems] =
    useState<Set<string> | null>(null)
  // why: the selected store's ACTUAL per-store override (days/items/source),
  // not the combination default. The By Store editor seeds, displays, and
  // diffs against this so a store's own customization is visible and saves
  // are detected correctly.
  const [storeCustomization, setStoreCustomization] = useState<{
    deliveryDays: number[]
    deliveryItems: string[]
    isCustomized: boolean
  } | null>(null)
  const [revertingStore, setRevertingStore] = useState(false)
  const [customizedStores, setCustomizedStores] = useState<Set<number>>(
    new Set(),
  )
  const [detailView, setDetailView] = useState<'combination' | 'store'>(
    'combination',
  )
  const [storeSearch, setStoreSearch] = useState('')
  const [storeSortKey, setStoreSortKey] = useState<
    | 'code-asc'
    | 'code-desc'
    | 'name-asc'
    | 'name-desc'
    | 'days-asc'
    | 'days-desc'
  >('days-asc')
  const [editingDeliveryItems, setEditingDeliveryItems] = useState<
    string[] | null
  >(null)
  const [addingCombo, setAddingCombo] = useState(false)
  const [newComboDays, setNewComboDays] = useState<Set<number>>(new Set())
  const [addingComboLoading, setAddingComboLoading] = useState(false)
  const [assignStoreId, setAssignStoreId] = useState<number | null>(null)
  const [assignComboId, setAssignComboId] = useState<number | null>(null)
  const [assigningSaving, setAssigningSaving] = useState(false)

  const regularSchedules = useMemo(
    () => schedules.filter((s) => s.scheduleType === 'regular'),
    [schedules],
  )
  const eventSchedules = useMemo(
    () => schedules.filter((s) => s.scheduleType === 'event'),
    [schedules],
  )

  // Find stores not covered by any active/upcoming schedule
  const assignedStoreIds = useMemo(() => {
    const ids = new Set<number>()
    for (const s of schedules) {
      if (s.status === 'active' || s.status === 'upcoming') {
        for (const id of s.storeIds || []) ids.add(id)
      }
    }
    return ids
  }, [schedules])

  // why: filter out legacy / admin / placeholder rows from the Unassigned tab
  // so admins see only real stores that genuinely need a delivery schedule.
  // Patterns identified in the location table (Q8 investigation 2026-05-26):
  //   - Corporate/admin codes starting with 0/9 ("0010-LOI Pipeline", "9000-Corporate")
  //   - Explicitly tagged "DO NOT USE", "DONT USE", "INACTIVE", "PIPELINE"
  //   - Pure-text placeholder rows like "Tustin", "GCNew", "Ops Events", "Relocation New Build"
  // NROs (real future stores with no delivery days yet) are kept visible since
  // they need scheduling before opening.
  const isLegacyOrAdminLocation = useCallback(
    (loc: { code: string; name: string }) => {
      const code = (loc.code || '').toUpperCase()
      const name = (loc.name || '').toUpperCase()
      if (/^(00|9)\d/.test(code)) return true // 00xx LOI/corporate, 9xxx admin
      if (code === '999') return true
      if (
        /DO NOT USE|DONT USE|INACTIVE|PIPELINE|^GCNEW$|^TUSTIN$|^OPS EVENTS$|RELOCATION NEW BUILD/.test(
          code,
        )
      )
        return true
      if (/DO NOT USE|DONT USE|INACTIVE|PIPELINE|DEAD PROJECTS/.test(name))
        return true
      return false
    },
    [],
  )

  const unassignedStores = useMemo(
    () =>
      allLocations
        .filter((l) => !assignedStoreIds.has(l.id))
        .filter((l) => !isLegacyOrAdminLocation(l)),
    [allLocations, assignedStoreIds, isLegacyOrAdminLocation],
  )

  // why: fetch the set of location IDs that have any per-store customization
  // (manual prep schedule OR manual safe-items override). Used to render
  // "Customized" badges on store rows and per-combo counts.
  const fetchCustomizedStores = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/customized_stores`,
      )
      const ids = new Set<number>(
        (res.data?.customized || []).map((c: any) => c.location_id),
      )
      setCustomizedStores(ids)
    } catch {
      // ignore — badges will simply not render
    }
  }, [])

  useEffect(() => {
    fetchCustomizedStores()
  }, [fetchCustomizedStores])

  const loadDetail = useCallback(async (scheduleId: number) => {
    setSelectedScheduleId(scheduleId)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BE_BASE_URL}/graphql`,
        {
          query: `{ getDeliveryScheduleDetail(iScheduleId: ${scheduleId}) {
              nodes { scheduleId scheduleName scheduleType startDate endDate status storeIds
                combinationId deliveryDays deliveryItems scheduleData warnings combinationSource
                storesOnProvisionalGrid }
            } }`,
        },
      )
      setScheduleDetail(res.data?.data?.getDeliveryScheduleDetail?.nodes || [])
    } catch {
      setScheduleDetail(null)
    }
  }, [])

  // why: Schedule Dates editor state — admin can change start/end dates from
  // the detail panel. Empty string for end means "ongoing" (null in DB).
  const [editingStartDate, setEditingStartDate] = useState<string | null>(null)
  const [editingEndDate, setEditingEndDate] = useState<string | null>(null)
  const [savingDates, setSavingDates] = useState(false)

  // why: Prep Base editor state — admin can view, edit, save, save+recompute
  // a schedule's per-schedule base. Loaded on-demand when the section is
  // expanded.
  const [baseExpanded, setBaseExpanded] = useState(false)
  const [baseGrid, setBaseGrid] = useState<
    Array<{
      prep_item_id: string
      prep_item_name: string
      sunday: boolean
      monday: boolean
      tuesday: boolean
      wednesday: boolean
      thursday: boolean
      friday: boolean
      saturday: boolean
    }>
  >([])
  const [baseDirty, setBaseDirty] = useState(false)
  const [baseIsCustomized, setBaseIsCustomized] = useState(false)
  const [savingBase, setSavingBase] = useState(false)
  const [baseMessage, setBaseMessage] = useState<string | null>(null)
  const [recomputing, setRecomputing] = useState(false)

  // why: recompute rewrites every combo's schedule_data and cascades to
  // per-store grids. Any open inline editor (combo grid, dates, add-combo,
  // per-store) is showing pre-recompute data — close them so the admin
  // can't save stale state on top of the in-flight rewrite.
  useEffect(() => {
    if (!recomputing) return
    setEditingComboId(null)
    setEditingGrid(null)
    setEditingDeliveryItems(null)
    setAddingCombo(false)
    setNewComboDays(new Set())
    setEditingStartDate(null)
    setEditingEndDate(null)
    setEditingStore(false)
    setEditingStoreGrid(null)
    setBaseExpanded(false)
  }, [recomputing])

  const handleSaveDates = useCallback(
    async (scheduleId: number) => {
      const startDate = editingStartDate
      const endDate = editingEndDate
      if (!startDate) return
      setSavingDates(true)
      try {
        await axios.post(
          `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/${scheduleId}/dates`,
          { start_date: startDate, end_date: endDate || null },
        )
        setEditingStartDate(null)
        setEditingEndDate(null)
        onRefresh()
      } finally {
        setSavingDates(false)
      }
    },
    [editingStartDate, editingEndDate, onRefresh],
  )

  const loadBase = useCallback(async (scheduleId: number) => {
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/${scheduleId}/base`,
      )
      const grid = (resp.data?.grid || []).map((r: any) => ({
        prep_item_id: r.prep_item_id,
        prep_item_name: r.prep_item_name,
        sunday: !!r.sunday,
        monday: !!r.monday,
        tuesday: !!r.tuesday,
        wednesday: !!r.wednesday,
        thursday: !!r.thursday,
        friday: !!r.friday,
        saturday: !!r.saturday,
      }))
      setBaseGrid(grid)
      setBaseIsCustomized(!!resp.data?.is_customized)
      setBaseDirty(false)
    } catch {
      setBaseGrid([])
      setBaseIsCustomized(false)
    }
  }, [])

  const handleSaveBase = useCallback(
    async (scheduleId: number, withRecompute: boolean) => {
      if (withRecompute) setRecomputing(true)
      else setSavingBase(true)
      try {
        await axios.post(
          `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/${scheduleId}/base`,
          {
            weekdays: baseGrid.map((r) => ({
              prep_item_id: r.prep_item_id,
              sunday: r.sunday,
              monday: r.monday,
              tuesday: r.tuesday,
              wednesday: r.wednesday,
              thursday: r.thursday,
              friday: r.friday,
              saturday: r.saturday,
            })),
            recompute: withRecompute,
          },
        )
        setBaseDirty(false)
        setBaseIsCustomized(true)
        if (withRecompute) {
          setBaseMessage(
            'Base saved and combinations recomputed for this schedule.',
          )
          onRefresh()
        } else {
          setBaseMessage(
            'Base saved. Existing stores keep their current grids. Click "Save + Recompute" to apply the new base to them too.',
          )
        }
      } finally {
        setSavingBase(false)
        setRecomputing(false)
      }
    },
    [baseGrid, onRefresh],
  )

  const handleDeactivate = useCallback(
    async (scheduleId: number) => {
      if (
        !window.confirm(
          'Deactivate this schedule? Stores will fall back to global defaults.',
        )
      )
        return
      try {
        // why: deactivation under the v2 model = set end_date to yesterday.
        // There's no more 'status=replaced' string column.
        await axios.post(
          `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/deactivate_schedule`,
          { schedule_id: scheduleId },
        )
        onRefresh()
        setSelectedScheduleId(null)
        setScheduleDetail(null)
      } catch {
        // Error handling
      }
    },
    [onRefresh],
  )

  const handleReactivate = useCallback(
    async (scheduleId: number) => {
      try {
        await axios.post(
          `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/reactivate_schedule`,
          { schedule_id: scheduleId },
        )
        onRefresh()
        loadDetail(scheduleId)
      } catch {
        // Error handling
      }
    },
    [onRefresh, loadDetail],
  )

  // why: scheduleDetail's storeIds is the schedule-wide list (same for every
  // combination row), so it can't identify a store's combination. Resolve by
  // matching the store's actual delivery_days pattern against each combination.
  const getStoreComboInfo = useCallback(
    (
      storeId: number,
    ): { deliveryDays: number[]; deliveryItems: string[] } | null => {
      if (!scheduleDetail) return null
      const storeLoc = locations.find((l) => l.locationId === storeId)
      const storeDays = parseDeliveryDays(storeLoc?.deliveryDays)
      if (storeDays.length === 0) return null
      for (const d of scheduleDetail) {
        if (!d.combinationId) continue
        const comboDays = [...(d.deliveryDays || [])].sort((a, b) => a - b)
        if (arrayEqual(comboDays, storeDays)) {
          return {
            deliveryDays: d.deliveryDays || [],
            deliveryItems: (d.deliveryItems as string[]) || [],
          }
        }
      }
      return null
    },
    [scheduleDetail, locations],
  )

  // why: the editor's true baseline is the store's own override
  // (storeCustomization) when loaded; the combination default is only a
  // fallback for a store with no override yet. Reading the combo as the
  // baseline made per-store safe-items invisible and broke save detection.
  const getStoreBaseline = useCallback(
    (storeId: number): { deliveryDays: number[]; deliveryItems: string[] } => {
      const combo = getStoreComboInfo(storeId)
      return {
        deliveryDays: storeCustomization?.deliveryDays?.length
          ? storeCustomization.deliveryDays
          : combo?.deliveryDays || [],
        deliveryItems: storeCustomization
          ? storeCustomization.deliveryItems
          : combo?.deliveryItems || [],
      }
    },
    [getStoreComboInfo, storeCustomization],
  )

  // Find which combination a store belongs to based on delivery days
  const getStoreComboGrid = useCallback(
    (storeId: number): any[] | null => {
      if (!scheduleDetail) return null
      const storeLoc = locations.find((l) => l.locationId === storeId)
      const storeDays = parseDeliveryDays(storeLoc?.deliveryDays)
      if (storeDays.length === 0) return null

      for (const d of scheduleDetail) {
        if (!d.combinationId || !d.scheduleData) continue
        const comboDays = [...(d.deliveryDays || [])].sort((a, b) => a - b)
        if (arrayEqual(comboDays, storeDays)) {
          return (d.scheduleData as any[]).map((item: any) => ({
            prepItemIdCode: item.prep_item_id,
            prepItemName: item.prep_item_name,
            scheduleName: 'Standard',
            isActivated: true,
            sunday: item.sunday || false,
            monday: item.monday || false,
            tuesday: item.tuesday || false,
            wednesday: item.wednesday || false,
            thursday: item.thursday || false,
            friday: item.friday || false,
            saturday: item.saturday || false,
            source: 'combination',
          }))
        }
      }
      return null
    },
    [scheduleDetail, locations],
  )

  // why: fetch the store's ACTUAL override (safe-items + delivery days +
  // source) for the active schedule. Returns null on failure so callers fall
  // back to the combination default.
  const fetchStoreCustomization = useCallback(
    async (storeId: number, scheduleId: number) => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/store_customization`,
          {
            params: { location_id: storeId, delivery_schedule_id: scheduleId },
          },
        )
        const parsed = {
          deliveryDays: (res.data?.delivery_days as number[]) || [],
          deliveryItems: (res.data?.delivery_items as string[]) || [],
          isCustomized: res.data?.is_customized === true,
        }
        setStoreCustomization(parsed)
        return parsed
      } catch {
        setStoreCustomization(null)
        return null
      }
    },
    [],
  )

  // Load store prep schedule (with combination fallback)
  const loadStoreSchedule = useCallback(
    async (storeId: number) => {
      setSelectedStoreId(storeId)
      setStoreScheduleLoading(true)
      setEditingStore(false)
      setEditingStoreGrid(null)
      setStoreCustomization(null)
      if (selectedScheduleId) {
        fetchStoreCustomization(storeId, selectedScheduleId)
      }
      try {
        const today = new Date().toISOString().split('T')[0]
        const res = await axios.post(
          `${process.env.REACT_APP_BE_BASE_URL}/graphql`,
          {
            query: `{ getStorePrepSchedule(iQueryDate: "${today}", iLocationId: ${storeId}) {
              nodes { prepItemIdCode prepItemName scheduleName isActivated
                sunday monday tuesday wednesday thursday friday saturday source }
            } }`,
          },
        )
        let nodes = res.data?.data?.getStorePrepSchedule?.nodes || []

        // Fallback to combination data if no per-store data
        if (nodes.length === 0) {
          const comboGrid = getStoreComboGrid(storeId)
          if (comboGrid) nodes = comboGrid
        }

        setStoreScheduleData(nodes)
        setEditingStoreGrid(nodes.map((n: any) => ({ ...n })))
      } catch {
        setStoreScheduleData(null)
      } finally {
        setStoreScheduleLoading(false)
      }
    },
    [getStoreComboGrid, selectedScheduleId, fetchStoreCustomization],
  )

  // why: the "Customized" badge is derived from a store having a manual
  // override, so an edit that lands the store back on its combination default
  // must clear the override — not create a redundant manual one. Returns true
  // only when the edited grid + delivery days + safe-items all match the
  // combination default, so Save can route to revert instead.
  const editsMatchComboDefault = useCallback(
    (storeId: number): boolean => {
      const combo = getStoreComboInfo(storeId)
      const comboGrid = getStoreComboGrid(storeId)
      // ponytail: no matching combination (e.g. store's days differ from every
      // combo) => can't know the default, so treat as a real customization.
      if (!combo || !comboGrid || !editingStoreGrid) return false

      const comboDays = [...combo.deliveryDays].sort((a, b) => a - b)
      const editedDays = editingStoreDeliveryDays
        ? Array.from(editingStoreDeliveryDays).sort((a, b) => a - b)
        : comboDays
      if (!arrayEqual(comboDays, editedDays)) return false

      const comboItems = new Set(combo.deliveryItems)
      const editedItems = editingStoreSafeItems ?? comboItems
      if (
        comboItems.size !== editedItems.size ||
        [...editedItems].some((it) => !comboItems.has(it))
      )
        return false

      const dayKeys = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ] as const
      const comboByItem = new Map(
        comboGrid.map((r: any) => [r.prepItemIdCode, r]),
      )
      if (comboByItem.size !== editingStoreGrid.length) return false
      return editingStoreGrid.every((row: any) => {
        const comboRow = comboByItem.get(row.prepItemIdCode)
        if (!comboRow) return false
        return dayKeys.every((d) => !!row[d] === !!comboRow[d])
      })
    },
    [
      getStoreComboInfo,
      getStoreComboGrid,
      editingStoreGrid,
      editingStoreDeliveryDays,
      editingStoreSafeItems,
    ],
  )

  // Save store prep schedule override
  const handleSaveStoreGrid = useCallback(async () => {
    if (!selectedStoreId || !editingStoreGrid) return
    setSavingStoreGrid(true)
    try {
      // why: if the edits bring the store back to its combination default,
      // revert (clears the manual override) instead of writing a redundant
      // manual record that would keep the "Customized" badge lit.
      if (selectedScheduleId && editsMatchComboDefault(selectedStoreId)) {
        await axios.post(
          `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/revert_store_customization`,
          {
            location_id: selectedStoreId,
            delivery_schedule_id: selectedScheduleId,
          },
        )
        setEditingStore(false)
        setEditingStoreDeliveryDays(null)
        setEditingStoreSafeItems(null)
        loadStoreSchedule(selectedStoreId)
        fetchCustomizedStores()
        loadDetail(selectedScheduleId)
        onRefresh()
        return
      }
      // why: delivery-day change must run first - it deactivates old
      // store_delivery_days rows and re-seeds the prep schedule from
      // overrides. Saving the prep grid afterward preserves the user's
      // per-item edits on top of that fresh seed.
      if (editingStoreDeliveryDays && selectedScheduleId) {
        const baseline = getStoreBaseline(selectedStoreId)
        const currentDays = baseline.deliveryDays.slice().sort((a, b) => a - b)
        const nextDays = Array.from(editingStoreDeliveryDays).sort(
          (a, b) => a - b,
        )
        const changed =
          currentDays.length !== nextDays.length ||
          currentDays.some((d, i) => d !== nextDays[i])
        if (changed && nextDays.length > 0) {
          await axios.post(
            `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/add_store_to_schedule`,
            {
              schedule_id: selectedScheduleId,
              location_id: selectedStoreId,
              delivery_days: nextDays,
            },
          )
        }
      }

      const weekdays = editingStoreGrid.map((r: any) => ({
        prep_item_id: r.prepItemIdCode,
        sunday: r.sunday,
        monday: r.monday,
        tuesday: r.tuesday,
        wednesday: r.wednesday,
        thursday: r.thursday,
        friday: r.friday,
        saturday: r.saturday,
      }))
      // why: route now keys on delivery_schedule_id (FK to delivery_schedule)
      // instead of the legacy schedule_name string. prep_base resolution
      // happens server-side from prep_item_schedule_config_2.
      if (!selectedScheduleId) {
        throw new Error('Cannot save: no schedule selected')
      }
      await axios.post(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/save_prep_schedule`,
        {
          location_id: selectedStoreId,
          delivery_schedule_id: selectedScheduleId,
          weekdays,
          source: 'manual',
        },
      )

      // why: persist per-store safe-items override if the user changed the
      // chip selection. Sets delivery_items_source='manual' so future combo
      // cascades skip this store (cascade protection ticket).
      if (editingStoreSafeItems) {
        const currentItems = new Set(
          getStoreBaseline(selectedStoreId).deliveryItems,
        )
        const nextItems = editingStoreSafeItems
        // why: diff both directions against the store's real baseline so a pure
        // removal (subset of current) is detected, not just additions.
        const changed =
          currentItems.size !== nextItems.size ||
          [...nextItems].some((it) => !currentItems.has(it)) ||
          [...currentItems].some((it) => !nextItems.has(it))
        if (changed) {
          await axios.post(
            `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/save_store_delivery_items`,
            {
              location_id: selectedStoreId,
              delivery_items: Array.from(nextItems),
            },
          )
        }
      }

      setEditingStore(false)
      setEditingStoreDeliveryDays(null)
      setEditingStoreSafeItems(null)
      loadStoreSchedule(selectedStoreId)
      fetchCustomizedStores()
      if (selectedScheduleId) {
        loadDetail(selectedScheduleId)
        onRefresh()
      }
    } catch {
      // Error handling
    } finally {
      setSavingStoreGrid(false)
    }
  }, [
    selectedStoreId,
    selectedScheduleId,
    editingStoreGrid,
    editingStoreDeliveryDays,
    editingStoreSafeItems,
    editsMatchComboDefault,
    fetchCustomizedStores,
    getStoreBaseline,
    loadStoreSchedule,
    loadDetail,
    onRefresh,
  ])

  // why: revert the selected store to its combination default — clears the
  // manual prep + safe-items override server-side so the "Customized" badge
  // stops rendering (the badge is driven by /customized_stores).
  const handleRevertStore = useCallback(async () => {
    if (!selectedStoreId || !selectedScheduleId) return
    setRevertingStore(true)
    try {
      await axios.post(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/revert_store_customization`,
        {
          location_id: selectedStoreId,
          delivery_schedule_id: selectedScheduleId,
        },
      )
      setEditingStore(false)
      setEditingStoreDeliveryDays(null)
      setEditingStoreSafeItems(null)
      loadStoreSchedule(selectedStoreId)
      fetchCustomizedStores()
      loadDetail(selectedScheduleId)
      onRefresh()
    } catch {
      // Error handling
    } finally {
      setRevertingStore(false)
    }
  }, [
    selectedStoreId,
    selectedScheduleId,
    fetchCustomizedStores,
    loadStoreSchedule,
    loadDetail,
    onRefresh,
  ])

  // Toggle safe item in combo edit mode
  const toggleComboSafeItem = useCallback(
    (itemId: string) => {
      if (!editingGrid || !editingDeliveryItems) return
      const items = new Set(editingDeliveryItems)
      if (items.has(itemId)) items.delete(itemId)
      else items.add(itemId)
      const newItems = Array.from(items)
      setEditingDeliveryItems(newItems)

      const combo = scheduleDetail?.find(
        (d) => d.combinationId === editingComboId,
      )
      const deliveryDays = combo?.deliveryDays || []

      setEditingGrid(
        (prev) =>
          prev?.map((g: any) => {
            const isSafe = newItems.includes(g.prep_item_id)
            const updated: any = { ...g, is_safe: isSafe }
            // When un-safeing, force delivery day columns OFF
            if (!isSafe) {
              for (const dayNum of deliveryDays) {
                const dayKey = DAY_NUM_TO_KEY[dayNum]
                if (dayKey) updated[dayKey] = false
              }
            }
            return updated
          }) || null,
      )
    },
    [editingGrid, editingDeliveryItems, editingComboId, scheduleDetail],
  )

  // Toggle grid day in combo edit mode
  const toggleComboGridDay = useCallback(
    (prepItemId: string, day: DayKey) => {
      if (!editingGrid) return
      const combo = scheduleDetail?.find(
        (d) => d.combinationId === editingComboId,
      )
      const deliveryDays = combo?.deliveryDays || []

      setEditingGrid(
        (prev) =>
          prev?.map((g: any) => {
            if (g.prep_item_id !== prepItemId) return g
            const dayNum = DAY_KEY_TO_NUM[day]
            if (!g.is_safe && deliveryDays.includes(dayNum)) return g
            return { ...g, [day]: !g[day] }
          }) || null,
      )
    },
    [editingGrid, editingComboId, scheduleDetail],
  )

  // Save edited combo grid
  const handleSaveComboGrid = useCallback(async () => {
    if (!editingComboId || !editingGrid) return
    setSavingGrid(true)
    try {
      await axios.post(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/update_combination`,
        {
          combination_id: editingComboId,
          delivery_items: editingDeliveryItems,
          schedule_data: editingGrid,
        },
      )
      if (selectedScheduleId) await loadDetail(selectedScheduleId)
      setEditingComboId(null)
      setEditingGrid(null)
      setEditingDeliveryItems(null)
    } catch {
      // Error handling
    } finally {
      setSavingGrid(false)
    }
  }, [
    editingComboId,
    editingGrid,
    editingDeliveryItems,
    selectedScheduleId,
    loadDetail,
  ])

  // Add new combination to existing schedule
  const handleAddCombination = useCallback(async () => {
    if (!selectedScheduleId || newComboDays.size === 0) return
    setAddingComboLoading(true)
    try {
      const days = Array.from(newComboDays).sort()
      const grid = allPrepItems.map((item) => ({
        prep_item_id: item.id,
        prep_item_name: item.name,
        is_safe: globalSafeItems.has(item.id),
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
      }))
      await axios.post(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/add_combination`,
        {
          schedule_id: selectedScheduleId,
          delivery_days: days,
          delivery_items: Array.from(globalSafeItems),
          schedule_data: grid,
        },
      )
      await loadDetail(selectedScheduleId)
      setAddingCombo(false)
      setNewComboDays(new Set())
    } catch {
      // Error handling
    } finally {
      setAddingComboLoading(false)
    }
  }, [
    selectedScheduleId,
    newComboDays,
    allPrepItems,
    globalSafeItems,
    loadDetail,
  ])

  // Assign a store to a schedule + combination
  const handleAssignStore = useCallback(async () => {
    if (!assignStoreId || !assignComboId || !selectedScheduleId) return
    setAssigningSaving(true)
    try {
      await axios.post(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/add_store_to_schedule`,
        {
          schedule_id: selectedScheduleId,
          combination_id: assignComboId,
          location_id: assignStoreId,
        },
      )
      onRefresh()
      if (selectedScheduleId) await loadDetail(selectedScheduleId)
      setAssignStoreId(null)
      setAssignComboId(null)
    } catch {
      // Error handling
    } finally {
      setAssigningSaving(false)
    }
  }, [assignStoreId, assignComboId, selectedScheduleId, onRefresh, loadDetail])

  // Calendar
  const calendarDays = useMemo(() => {
    const { year, month } = viewMonth
    const firstDay = new Date(year, month, 1)
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    let startDow = firstDay.getDay() - 1
    if (startDow < 0) startDow = 6

    const days: Array<{ date: string; day: number; inMonth: boolean }> = []
    for (let i = 0; i < startDow; i++)
      days.push({ date: '', day: 0, inMonth: false })
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(
        d,
      ).padStart(2, '0')}`
      days.push({ date: dateStr, day: d, inMonth: true })
    }
    while (days.length % 7 !== 0)
      days.push({ date: '', day: 0, inMonth: false })
    return days
  }, [viewMonth])

  // Map dates to schedules for calendar coloring
  const dateScheduleMap = useMemo(() => {
    const map = new Map<string, { type: string; name: string; id: number }>()
    for (const day of calendarDays) {
      if (!day.inMonth) continue
      // Check events first
      for (const s of eventSchedules) {
        if (
          (s.status === 'active' || s.status === 'upcoming') &&
          s.endDate &&
          day.date >= s.startDate &&
          day.date <= s.endDate
        ) {
          map.set(day.date, {
            type: 'event',
            name: s.scheduleName,
            id: s.scheduleId,
          })
          break
        }
      }
      if (map.has(day.date)) continue
      // Check regular
      for (const s of regularSchedules) {
        if (
          (s.status === 'active' || s.status === 'upcoming') &&
          day.date >= s.startDate
        ) {
          map.set(day.date, {
            type: 'regular',
            name: s.scheduleName,
            id: s.scheduleId,
          })
          break
        }
      }
    }
    return map
  }, [calendarDays, regularSchedules, eventSchedules])

  const today = new Date().toISOString().split('T')[0]
  const selectedSchedule = schedules.find(
    (s) => s.scheduleId === selectedScheduleId,
  )

  // why: the count is schedule-wide, so every combination row repeats it.
  const provisionalStoreCount =
    scheduleDetail?.[0]?.storesOnProvisionalGrid ?? 0

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left: Calendar */}
        <Box sx={{ flex: '0 0 300px' }}>
          {/* Month nav */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={() =>
                setViewMonth((p) =>
                  p.month === 0
                    ? { year: p.year - 1, month: 11 }
                    : { ...p, month: p.month - 1 },
                )
              }
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              sx={{ fontWeight: 600, minWidth: 150, textAlign: 'center' }}
            >
              {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
            </Typography>
            <IconButton
              size="small"
              onClick={() =>
                setViewMonth((p) =>
                  p.month === 11
                    ? { year: p.year + 1, month: 0 }
                    : { ...p, month: p.month + 1 },
                )
              }
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '2px',
              mb: 2,
            }}
          >
            {DAY_HEADERS.map((d) => (
              <Box
                key={d}
                sx={{
                  textAlign: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#888',
                  py: 0.5,
                }}
              >
                {d}
              </Box>
            ))}
            {calendarDays.map((cell, idx) => {
              if (!cell.inMonth) return <Box key={idx} sx={{ height: 32 }} />
              const sched = dateScheduleMap.get(cell.date)
              const isToday = cell.date === today
              const isSelected = sched?.id === selectedScheduleId
              return (
                <Box
                  key={idx}
                  onClick={() => sched && loadDetail(sched.id)}
                  sx={{
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 0.5,
                    cursor: sched ? 'pointer' : 'default',
                    backgroundColor: sched
                      ? sched.type === 'event'
                        ? '#fff3e0'
                        : '#e3f2fd'
                      : 'transparent',
                    border: isSelected
                      ? '2px solid #1976d2'
                      : isToday
                      ? '2px solid #999'
                      : '2px solid transparent',
                    fontSize: 12,
                    fontWeight: isToday ? 700 : 400,
                    '&:hover': sched ? { opacity: 0.8 } : {},
                  }}
                  title={sched ? sched.name : undefined}
                >
                  {cell.day}
                </Box>
              )
            })}
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 0.5,
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #90caf9',
                }}
              />
              <Typography variant="caption">Regular</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 0.5,
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ffcc80',
                }}
              />
              <Typography variant="caption">Event</Typography>
            </Box>
          </Box>
        </Box>

        {/* Right: Schedule list + detail */}
        <Box sx={{ flex: 1 }}>
          {/* Sub tabs */}
          <Tabs
            value={subTab}
            onChange={(_e, v) => setSubTab(v)}
            sx={{
              mb: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: 13,
                minHeight: 36,
              },
            }}
          >
            <Tab label={`Regular (${regularSchedules.length})`} />
            <Tab label={`Events (${eventSchedules.length})`} />
            <Tab label={`Unassigned (${unassignedStores.length})`} />
          </Tabs>

          {/* Regular schedules */}
          {subTab === 0 && (
            <Box>
              {regularSchedules.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No regular schedules. Upload a delivery file to create one.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {regularSchedules.map((s) => (
                    <Box
                      key={s.scheduleId}
                      onClick={() => loadDetail(s.scheduleId)}
                      sx={{
                        p: 1.5,
                        border:
                          selectedScheduleId === s.scheduleId
                            ? '2px solid #1976d2'
                            : '1px solid #e0e0e0',
                        borderRadius: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor:
                          selectedScheduleId === s.scheduleId
                            ? '#e3f2fd'
                            : 'white',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.scheduleName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.storeCount} stores - {s.combinationCount}{' '}
                          combinations
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {new Date(s.startDate).toLocaleDateString()} →
                        </Typography>
                        <Chip
                          label={s.status}
                          size="small"
                          color={
                            s.status === 'active'
                              ? 'success'
                              : s.status === 'upcoming'
                              ? 'info'
                              : 'default'
                          }
                          variant="outlined"
                          sx={{ height: 22, fontSize: 10 }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Event schedules */}
          {subTab === 1 && (
            <Box>
              {eventSchedules.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No event schedules. Create one from the Import & Setup tab
                  using the "Event" type.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {eventSchedules.map((s) => (
                    <Box
                      key={s.scheduleId}
                      onClick={() => loadDetail(s.scheduleId)}
                      sx={{
                        p: 1.5,
                        border:
                          selectedScheduleId === s.scheduleId
                            ? '2px solid #ed6c02'
                            : '1px solid #e0e0e0',
                        borderRadius: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor:
                          selectedScheduleId === s.scheduleId
                            ? '#fff3e0'
                            : 'white',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.scheduleName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.storeCount} stores - {s.combinationCount}{' '}
                          combinations
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {new Date(s.startDate).toLocaleDateString()} -{' '}
                          {s.endDate
                            ? new Date(s.endDate).toLocaleDateString()
                            : ''}
                        </Typography>
                        <Chip
                          label={s.status}
                          size="small"
                          color={
                            s.status === 'active'
                              ? 'warning'
                              : s.status === 'upcoming'
                              ? 'info'
                              : 'default'
                          }
                          variant="outlined"
                          sx={{ height: 22, fontSize: 10 }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Unassigned stores */}
          {subTab === 2 && (
            <Box>
              {unassignedStores.length === 0 ? (
                <Alert severity="success">
                  All stores have a schedule assigned.
                </Alert>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    These stores are using global defaults and have no specific
                    schedule.
                    {selectedSchedule &&
                      ' Click a store to assign it to the selected schedule.'}
                  </Typography>

                  {/* Assign store inline dialog */}
                  {assignStoreId && selectedSchedule && scheduleDetail && (
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        border: '1px solid #1976d2',
                        borderRadius: 1,
                        backgroundColor: '#f5f9ff',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Assign{' '}
                        {allLocations.find((l) => l.id === assignStoreId)?.code}{' '}
                        -{' '}
                        {allLocations.find((l) => l.id === assignStoreId)?.name}{' '}
                        to {selectedSchedule.scheduleName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: 'block' }}
                      >
                        Select a delivery combination:
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          mb: 1.5,
                        }}
                      >
                        {scheduleDetail
                          .filter((d) => d.combinationId)
                          .map((d) => (
                            <Chip
                              key={d.combinationId}
                              label={[...(d.deliveryDays || [])]
                                .sort()
                                .map((day) => DAY_LABELS[day])
                                .join(' / ')}
                              size="small"
                              color={
                                assignComboId === d.combinationId
                                  ? 'primary'
                                  : 'default'
                              }
                              variant={
                                assignComboId === d.combinationId
                                  ? 'filled'
                                  : 'outlined'
                              }
                              clickable
                              onClick={() => setAssignComboId(d.combinationId)}
                              sx={{ fontSize: 11 }}
                            />
                          ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!assignComboId || assigningSaving}
                          onClick={handleAssignStore}
                          sx={{ textTransform: 'none', fontSize: 11 }}
                        >
                          {assigningSaving ? (
                            <CircularProgress size={14} sx={{ mr: 0.5 }} />
                          ) : null}
                          Assign
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setAssignStoreId(null)
                            setAssignComboId(null)
                          }}
                          sx={{ textTransform: 'none', fontSize: 11 }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {unassignedStores.map((s) => (
                      <Chip
                        key={s.id}
                        label={`${s.code} - ${s.name}`}
                        size="small"
                        variant={assignStoreId === s.id ? 'filled' : 'outlined'}
                        color={assignStoreId === s.id ? 'primary' : 'default'}
                        clickable={!!selectedSchedule}
                        onClick={() => {
                          if (!selectedSchedule) return
                          setAssignStoreId(assignStoreId === s.id ? null : s.id)
                          setAssignComboId(null)
                        }}
                        sx={{ fontSize: 11 }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Schedule detail panel */}
      {selectedSchedule && (
        <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedSchedule.scheduleName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSchedule.scheduleType === 'regular'
                  ? 'Regular'
                  : 'Event'}{' '}
                - {new Date(selectedSchedule.startDate).toLocaleDateString()}
                {selectedSchedule.endDate
                  ? ` to ${new Date(
                      selectedSchedule.endDate,
                    ).toLocaleDateString()}`
                  : ' → ongoing'}{' '}
                - {selectedSchedule.storeCount} stores
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={selectedSchedule.status}
                size="small"
                color={
                  selectedSchedule.status === 'active'
                    ? 'success'
                    : selectedSchedule.status === 'upcoming'
                    ? 'info'
                    : 'default'
                }
                sx={{ height: 24 }}
              />
              {(selectedSchedule.status === 'active' ||
                selectedSchedule.status === 'upcoming') && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => handleDeactivate(selectedSchedule.scheduleId)}
                  disabled={recomputing}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  Deactivate
                </Button>
              )}
              {selectedSchedule.status === 'replaced' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => handleReactivate(selectedSchedule.scheduleId)}
                  disabled={recomputing}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  Reactivate
                </Button>
              )}
            </Box>
          </Box>

          {recomputing && (
            <Alert
              icon={<CircularProgress size={16} />}
              severity="info"
              sx={{ mb: 2 }}
            >
              Recomputing combinations… edits are locked until this finishes
              (usually 5–10s).
            </Alert>
          )}

          {/* ===== Schedule Dates editor ===== */}
          <Box
            sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                display: 'block',
                mb: 1,
              }}
            >
              Schedule Dates
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <TextField
                type="date"
                size="small"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={
                  editingStartDate ?? selectedSchedule.startDate.slice(0, 10)
                }
                onChange={(e) => setEditingStartDate(e.target.value)}
                sx={{ width: 170 }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                →
              </Typography>
              <TextField
                type="date"
                size="small"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={
                  editingEndDate ??
                  (selectedSchedule.endDate
                    ? selectedSchedule.endDate.slice(0, 10)
                    : '')
                }
                onChange={(e) => setEditingEndDate(e.target.value || null)}
                placeholder="ongoing"
                sx={{ width: 170 }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={
                  savingDates ||
                  recomputing ||
                  (editingStartDate === null && editingEndDate === null)
                }
                onClick={() => handleSaveDates(selectedSchedule.scheduleId)}
                sx={{ textTransform: 'none' }}
              >
                Save Dates
              </Button>
              {(editingStartDate !== null || editingEndDate !== null) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setEditingStartDate(null)
                    setEditingEndDate(null)
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Box>

          {/* ===== Prep Base section ===== */}
          <Box
            sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', flex: 1 }}
              >
                Prep Base —{' '}
                {baseIsCustomized
                  ? 'Customized for this schedule'
                  : 'Using Template'}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={recomputing}
                onClick={() => {
                  if (!baseExpanded) {
                    loadBase(selectedSchedule.scheduleId)
                  }
                  setBaseExpanded((v) => !v)
                }}
                sx={{ textTransform: 'none' }}
              >
                {baseExpanded ? 'Hide' : 'View / Edit Base'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={async () => {
                  if (
                    !window.confirm(
                      'Recompute all combinations for this schedule using the current base? Existing combo grids will be re-derived. Manual store overrides are preserved.',
                    )
                  )
                    return
                  setRecomputing(true)
                  try {
                    await axios.post(
                      `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/recompute_combinations`,
                      { schedule_id: selectedSchedule.scheduleId },
                    )
                    onRefresh()
                    // why: onRefresh reloads the schedule list, not the detail
                    // this panel renders from, so the recomputed grids and
                    // counts stay stale until the schedule is reselected.
                    await loadDetail(selectedSchedule.scheduleId)
                    // why: an open store panel renders its own fetched grid,
                    // so it keeps showing the pre-recompute days until reloaded.
                    if (selectedStoreId)
                      await loadStoreSchedule(selectedStoreId)
                    setBaseMessage('Combinations recomputed for this schedule.')
                  } finally {
                    setRecomputing(false)
                  }
                }}
                disabled={recomputing || savingBase}
                sx={{ textTransform: 'none' }}
              >
                Recompute Combinations
              </Button>
            </Box>
            {provisionalStoreCount > 0 && (
              <Alert severity="warning" sx={{ mb: 1, fontSize: 12 }}>
                {provisionalStoreCount} store
                {provisionalStoreCount === 1 ? ' is' : 's are'} using the grid
                as configured, not the one previewed in Step 3. Recompute
                Combinations applies the four combination rules and replaces it.
                Leave it if the arrangement is intentional.
              </Alert>
            )}
            {baseMessage && (
              <Alert
                severity="info"
                sx={{ mb: 1, fontSize: 12 }}
                onClose={() => setBaseMessage(null)}
              >
                {baseMessage}
              </Alert>
            )}
            {baseExpanded && baseGrid.length > 0 && (
              <>
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    overflow: 'auto',
                    mb: 1,
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 8px',
                            borderBottom: '2px solid #e0e0e0',
                          }}
                        >
                          Prep Item
                        </th>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                          (d) => (
                            <th
                              key={d}
                              style={{
                                textAlign: 'center',
                                padding: '4px 2px',
                                borderBottom: '2px solid #e0e0e0',
                                width: 48,
                              }}
                            >
                              {d}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {baseGrid.map((row, idx) => (
                        <tr key={row.prep_item_id}>
                          <td
                            style={{
                              padding: '4px 8px',
                              borderBottom: '1px solid #eee',
                            }}
                          >
                            {row.prep_item_name}
                          </td>
                          {(
                            [
                              'sunday',
                              'monday',
                              'tuesday',
                              'wednesday',
                              'thursday',
                              'friday',
                              'saturday',
                            ] as const
                          ).map((day) => (
                            <td
                              key={day}
                              style={{
                                padding: 0,
                                textAlign: 'center',
                                borderBottom: '1px solid #eee',
                              }}
                            >
                              <Checkbox
                                checked={!!row[day]}
                                size="small"
                                onChange={() => {
                                  setBaseGrid((prev) =>
                                    prev.map((r, i) =>
                                      i === idx ? { ...r, [day]: !r[day] } : r,
                                    ),
                                  )
                                  setBaseDirty(true)
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => loadBase(selectedSchedule.scheduleId)}
                    disabled={!baseDirty || savingBase || recomputing}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      handleSaveBase(selectedSchedule.scheduleId, false)
                    }
                    disabled={!baseDirty || savingBase || recomputing}
                    sx={{ textTransform: 'none' }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    onClick={() =>
                      handleSaveBase(selectedSchedule.scheduleId, true)
                    }
                    disabled={!baseDirty || savingBase || recomputing}
                    sx={{ textTransform: 'none' }}
                  >
                    Save + Recompute
                  </Button>
                </Box>
              </>
            )}
          </Box>

          {/* Detail view tabs: By Combination / By Store */}
          {selectedStoreId ? (
            /* Store drill-down view */
            <Box>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setSelectedStoreId(null)
                  setStoreScheduleData(null)
                  setEditingStore(false)
                }}
                sx={{ textTransform: 'none', mb: 1 }}
              >
                Back to schedule
              </Button>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {(() => {
                  const loc = allLocations.find((l) => l.id === selectedStoreId)
                  return loc
                    ? `${loc.code} - ${loc.name}`
                    : `Store #${selectedStoreId}`
                })()}
              </Typography>

              {storeScheduleLoading ? (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : storeScheduleData && storeScheduleData.length > 0 ? (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {!editingStore ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setEditingStore(true)
                          const baseline = getStoreBaseline(selectedStoreId!)
                          setEditingStoreDeliveryDays(
                            new Set(baseline.deliveryDays),
                          )
                          setEditingStoreSafeItems(
                            new Set(baseline.deliveryItems),
                          )
                        }}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                      >
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            savingStoreGrid ? (
                              <CircularProgress size={14} />
                            ) : (
                              <SaveIcon />
                            )
                          }
                          onClick={handleSaveStoreGrid}
                          disabled={
                            savingStoreGrid ||
                            (editingStoreDeliveryDays?.size ?? 1) === 0
                          }
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => {
                            setEditingStore(false)
                            setEditingStoreGrid(
                              storeScheduleData.map((n: any) => ({ ...n })),
                            )
                            setEditingStoreDeliveryDays(null)
                            setEditingStoreSafeItems(null)
                          }}
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          Cancel
                        </Button>
                        {storeCustomization?.isCustomized && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={
                              revertingStore ? (
                                <CircularProgress size={14} />
                              ) : (
                                <RestartAltIcon />
                              )
                            }
                            onClick={handleRevertStore}
                            disabled={revertingStore || savingStoreGrid}
                            sx={{ textTransform: 'none', fontSize: 12 }}
                          >
                            Revert to default
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                  {/* Delivery days editor - only visible while editing */}
                  {editingStore && editingStoreDeliveryDays && (
                    <Box
                      sx={{
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        Delivery Days:
                      </Typography>
                      {DAY_KEYS.map((day, i) => {
                        const dayNum = DAY_KEY_TO_NUM[day]
                        const selected = editingStoreDeliveryDays.has(dayNum)
                        return (
                          <Chip
                            key={day}
                            label={DAY_HEADERS[i]}
                            size="small"
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            icon={
                              selected ? (
                                <LocalShippingIcon sx={{ fontSize: 12 }} />
                              ) : undefined
                            }
                            onClick={() => {
                              const next = new Set(editingStoreDeliveryDays)
                              if (next.has(dayNum)) next.delete(dayNum)
                              else next.add(dayNum)
                              setEditingStoreDeliveryDays(next)
                            }}
                            sx={{ cursor: 'pointer', height: 24, fontSize: 11 }}
                          />
                        )
                      })}
                      {editingStoreDeliveryDays.size === 0 && (
                        <Typography variant="caption" color="error">
                          Select at least one day
                        </Typography>
                      )}
                    </Box>
                  )}
                  {/* Per-store safe items editor - only visible while editing */}
                  {editingStore && editingStoreSafeItems && (
                    <Box
                      sx={{
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        Safe Items (delivery-day prep allowed):
                      </Typography>
                      {allPrepItems.map((item) => {
                        const selected = editingStoreSafeItems.has(item.id)
                        return (
                          <Chip
                            key={item.id}
                            label={item.name}
                            size="small"
                            color={selected ? 'success' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => {
                              const next = new Set(editingStoreSafeItems)
                              if (next.has(item.id)) next.delete(item.id)
                              else next.add(item.id)
                              setEditingStoreSafeItems(next)
                            }}
                            sx={{ cursor: 'pointer', height: 22, fontSize: 11 }}
                          />
                        )
                      })}
                    </Box>
                  )}
                  {(() => {
                    const baseline = getStoreBaseline(selectedStoreId!)
                    const storeDeliveryDays = new Set(baseline.deliveryDays)
                    const storeDeliveryItems = new Set(baseline.deliveryItems)
                    return (
                      <Box
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          overflow: 'auto',
                        }}
                      >
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: 13,
                            minWidth: 600,
                          }}
                        >
                          <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                              <th
                                style={{
                                  textAlign: 'left',
                                  padding: '6px 12px',
                                  borderBottom: '2px solid #e0e0e0',
                                  minWidth: 140,
                                }}
                              >
                                Prep Item
                              </th>
                              {DAY_KEYS.map((day, i) => {
                                const dayNum = DAY_KEY_TO_NUM[day]
                                const isDelivery = storeDeliveryDays.has(dayNum)
                                return (
                                  <th
                                    key={day}
                                    style={{
                                      textAlign: 'center',
                                      padding: '6px 4px',
                                      borderBottom: '2px solid #e0e0e0',
                                      width: 55,
                                      backgroundColor: isDelivery
                                        ? '#e3f2fd'
                                        : '#f5f5f5',
                                    }}
                                  >
                                    {DAY_HEADERS[i]}
                                    {isDelivery && (
                                      <LocalShippingIcon
                                        sx={{
                                          fontSize: 10,
                                          ml: 0.3,
                                          color: 'primary.main',
                                          verticalAlign: 'middle',
                                        }}
                                      />
                                    )}
                                  </th>
                                )
                              })}
                              <th
                                style={{
                                  textAlign: 'center',
                                  padding: '6px 4px',
                                  borderBottom: '2px solid #e0e0e0',
                                  width: 45,
                                }}
                              >
                                Days
                              </th>
                              <th
                                style={{
                                  textAlign: 'center',
                                  padding: '6px 4px',
                                  borderBottom: '2px solid #e0e0e0',
                                  width: 55,
                                }}
                              >
                                Source
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(editingStore
                              ? editingStoreGrid
                              : storeScheduleData
                            )?.map((item: any) => {
                              const prepDays = DAY_KEYS.filter(
                                (d) => item[d],
                              ).length
                              const isZero = prepDays === 0
                              const isSafe = storeDeliveryItems.has(
                                item.prepItemIdCode,
                              )
                              return (
                                <tr
                                  key={item.prepItemIdCode}
                                  style={{
                                    backgroundColor: isZero
                                      ? '#ffebee'
                                      : 'transparent',
                                  }}
                                >
                                  <td
                                    style={{
                                      padding: '3px 12px',
                                      borderBottom: '1px solid #eee',
                                      fontWeight: isZero ? 600 : 400,
                                      color: isZero ? '#d32f2f' : 'inherit',
                                    }}
                                  >
                                    {item.prepItemName}
                                    {isSafe && (
                                      <Chip
                                        label="safe"
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        sx={{
                                          ml: 0.5,
                                          height: 16,
                                          fontSize: 9,
                                          '& .MuiChip-label': { px: 0.5 },
                                        }}
                                      />
                                    )}
                                  </td>
                                  {DAY_KEYS.map((day) => {
                                    const dayNum = DAY_KEY_TO_NUM[day]
                                    const isDelivery =
                                      storeDeliveryDays.has(dayNum)
                                    const isConstrained = !isSafe && isDelivery
                                    return (
                                      <td
                                        key={day}
                                        style={{
                                          textAlign: 'center',
                                          padding: '1px 4px',
                                          borderBottom: '1px solid #eee',
                                          backgroundColor: isDelivery
                                            ? isZero
                                              ? '#ffcdd2'
                                              : '#e3f2fd'
                                            : 'transparent',
                                        }}
                                      >
                                        <Checkbox
                                          size="small"
                                          checked={!!item[day]}
                                          disabled={
                                            !editingStore || isConstrained
                                          }
                                          onChange={() => {
                                            if (!editingStore || isConstrained)
                                              return
                                            setEditingStoreGrid(
                                              (prev) =>
                                                prev?.map((r: any) =>
                                                  r.prepItemIdCode ===
                                                  item.prepItemIdCode
                                                    ? {
                                                        ...r,
                                                        [day]: !r[day],
                                                      }
                                                    : r,
                                                ) || null,
                                            )
                                          }}
                                          sx={{
                                            p: 0.25,
                                            '& .MuiSvgIcon-root': {
                                              fontSize: 16,
                                            },
                                          }}
                                        />
                                      </td>
                                    )
                                  })}
                                  <td
                                    style={{
                                      textAlign: 'center',
                                      padding: '3px 4px',
                                      borderBottom: '1px solid #eee',
                                      fontWeight: 600,
                                      color: isZero ? '#d32f2f' : 'inherit',
                                    }}
                                  >
                                    {prepDays}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: 'center',
                                      padding: '3px 4px',
                                      borderBottom: '1px solid #eee',
                                    }}
                                  >
                                    <Chip
                                      label={item.source}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: 9 }}
                                    />
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </Box>
                    )
                  })()}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No prep schedule data for this store.
                </Typography>
              )}
            </Box>
          ) : (
            <>
              {/* Two-view tabs */}
              <Tabs
                value={detailView}
                onChange={(_e, v) => setDetailView(v)}
                sx={{
                  mb: 2,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: 13,
                    minHeight: 32,
                    py: 0,
                  },
                }}
              >
                <Tab label="By Combination" value="combination" />
                <Tab label="By Store" value="store" />
              </Tabs>

              {/* === By Store View === */}
              {detailView === 'store' && (
                <Box>
                  <TextField
                    size="small"
                    placeholder="Search stores..."
                    value={storeSearch}
                    onChange={(e) => setStoreSearch(e.target.value)}
                    sx={{ mb: 2, width: 300 }}
                  />
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 500,
                    }}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13,
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th
                            style={{
                              textAlign: 'left',
                              padding: '6px 12px',
                              borderBottom: '2px solid #e0e0e0',
                              width: 80,
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                            onClick={() =>
                              setStoreSortKey((k) =>
                                k === 'code-asc' ? 'code-desc' : 'code-asc',
                              )
                            }
                          >
                            Store #{' '}
                            {storeSortKey === 'code-asc'
                              ? '↑'
                              : storeSortKey === 'code-desc'
                              ? '↓'
                              : ''}
                          </th>
                          <th
                            style={{
                              textAlign: 'left',
                              padding: '6px 12px',
                              borderBottom: '2px solid #e0e0e0',
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                            onClick={() =>
                              setStoreSortKey((k) =>
                                k === 'name-asc' ? 'name-desc' : 'name-asc',
                              )
                            }
                          >
                            Store Name{' '}
                            {storeSortKey === 'name-asc'
                              ? '↑'
                              : storeSortKey === 'name-desc'
                              ? '↓'
                              : ''}
                          </th>
                          <th
                            style={{
                              textAlign: 'center',
                              padding: '6px 12px',
                              borderBottom: '2px solid #e0e0e0',
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                            onClick={() =>
                              setStoreSortKey((k) =>
                                k === 'days-asc' ? 'days-desc' : 'days-asc',
                              )
                            }
                          >
                            Delivery Days{' '}
                            {storeSortKey === 'days-asc'
                              ? '↑'
                              : storeSortKey === 'days-desc'
                              ? '↓'
                              : ''}
                          </th>
                          <th
                            style={{
                              textAlign: 'center',
                              padding: '6px 12px',
                              borderBottom: '2px solid #e0e0e0',
                              width: 80,
                            }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedSchedule.storeIds || [])
                          .map((storeId) =>
                            allLocations.find((l) => l.id === storeId),
                          )
                          .filter(Boolean)
                          .filter((loc) => {
                            if (!storeSearch.trim()) return true
                            const q = storeSearch.toLowerCase()
                            return (
                              loc!.code.toLowerCase().includes(q) ||
                              loc!.name.toLowerCase().includes(q)
                            )
                          })
                          .sort((a, b) => {
                            const ad =
                              locations.find((l) => l.locationId === a!.id)
                                ?.deliveryDays || ''
                            const bd =
                              locations.find((l) => l.locationId === b!.id)
                                ?.deliveryDays || ''
                            switch (storeSortKey) {
                              case 'code-asc':
                                return a!.code.localeCompare(
                                  b!.code,
                                  undefined,
                                  {
                                    numeric: true,
                                  },
                                )
                              case 'code-desc':
                                return b!.code.localeCompare(
                                  a!.code,
                                  undefined,
                                  {
                                    numeric: true,
                                  },
                                )
                              case 'name-asc':
                                return a!.name.localeCompare(b!.name)
                              case 'name-desc':
                                return b!.name.localeCompare(a!.name)
                              case 'days-asc':
                                return ad.localeCompare(bd)
                              case 'days-desc':
                                return bd.localeCompare(ad)
                              default:
                                return 0
                            }
                          })
                          .map((loc) => {
                            let comboLabel = '—'
                            const locData = locations.find(
                              (l) => l.locationId === loc!.id,
                            )
                            if (locData)
                              comboLabel = locData.deliveryDays || '—'
                            return (
                              <tr
                                key={loc!.id}
                                style={{
                                  cursor: recomputing
                                    ? 'not-allowed'
                                    : 'pointer',
                                  opacity: recomputing ? 0.5 : 1,
                                }}
                                onClick={() => {
                                  if (recomputing) return
                                  loadStoreSchedule(loc!.id)
                                }}
                              >
                                <td
                                  style={{
                                    padding: '4px 12px',
                                    borderBottom: '1px solid #eee',
                                  }}
                                >
                                  {loc!.code}
                                </td>
                                <td
                                  style={{
                                    padding: '4px 12px',
                                    borderBottom: '1px solid #eee',
                                  }}
                                >
                                  {loc!.name}
                                  {customizedStores.has(loc!.id) && (
                                    <Chip
                                      label="Customized"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{
                                        ml: 1,
                                        height: 18,
                                        fontSize: 10,
                                      }}
                                    />
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: '4px 12px',
                                    borderBottom: '1px solid #eee',
                                    textAlign: 'center',
                                  }}
                                >
                                  {comboLabel}
                                </td>
                                <td
                                  style={{
                                    padding: '4px 12px',
                                    borderBottom: '1px solid #eee',
                                    textAlign: 'center',
                                  }}
                                >
                                  <Button
                                    size="small"
                                    variant="text"
                                    sx={{ textTransform: 'none', fontSize: 11 }}
                                  >
                                    View
                                  </Button>
                                </td>
                              </tr>
                            )
                          })}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}

              {/* === By Combination View === */}
              {detailView === 'combination' && (
                <>
                  {/* Combinations — expandable */}
                  {scheduleDetail &&
                    scheduleDetail.length > 0 &&
                    scheduleDetail[0].combinationId && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Delivery Combinations
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {scheduleDetail
                            .filter((d) => d.combinationId)
                            .map((d) => {
                              const isExpanded =
                                expandedComboId === d.combinationId
                              const grid = (d.scheduleData as any[]) || []
                              const deliveryDays = new Set(d.deliveryDays || [])
                              // why: count stores matching this combination's
                              // delivery-day pattern and how many of those are
                              // customized — admins see at a glance which combos
                              // have meaningful per-store divergence.
                              const comboStoreIds = getComboStoreIds(
                                locations,
                                d.storeIds,
                                d.deliveryDays || undefined,
                              )
                              const comboStoreCount = comboStoreIds.length
                              const comboCustomizedCount = comboStoreIds.filter(
                                (id) => customizedStores.has(id),
                              ).length
                              return (
                                <Box key={d.combinationId}>
                                  <Box
                                    sx={{
                                      p: 1,
                                      border: '1px solid #eee',
                                      borderRadius: 0.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: '#fafafa' },
                                    }}
                                    onClick={() =>
                                      setExpandedComboId(
                                        isExpanded ? null : d.combinationId,
                                      )
                                    }
                                  >
                                    <ExpandMoreIcon
                                      sx={{
                                        fontSize: 18,
                                        transform: isExpanded
                                          ? 'rotate(180deg)'
                                          : 'none',
                                        transition: 'transform 0.2s',
                                        color: '#999',
                                      }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      {[...(d.deliveryDays || [])]
                                        .sort()
                                        .map((day) => (
                                          <Chip
                                            key={day}
                                            icon={
                                              <LocalShippingIcon
                                                sx={{
                                                  fontSize: '14px !important',
                                                }}
                                              />
                                            }
                                            label={DAY_LABELS[day] || day}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ height: 24, fontSize: 11 }}
                                          />
                                        ))}
                                    </Box>
                                    {/* Algorithm-constraint warnings badge */}
                                    {(d.warnings?.length || 0) > 0 && (
                                      <Chip
                                        title={(d.warnings || [])
                                          .map(
                                            (w) =>
                                              `${w.prep_item_id}: ${w.reason}`,
                                          )
                                          .join('\n')}
                                        label={`⚠ ${d.warnings!.length}`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          // toggle expand so user can see the
                                          // flagged item rows in the grid below
                                          setExpandedComboId(
                                            expandedComboId === d.combinationId
                                              ? null
                                              : d.combinationId,
                                          )
                                        }}
                                        sx={{
                                          height: 24,
                                          fontSize: 11,
                                          cursor: 'pointer',
                                        }}
                                      />
                                    )}
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ flex: 1 }}
                                    >
                                      {(d.deliveryItems as any[])?.length || 0}{' '}
                                      safe items - {grid.length} prep items{' '}
                                      &middot; {comboStoreCount} store
                                      {comboStoreCount === 1 ? '' : 's'}
                                      {comboCustomizedCount > 0 && (
                                        <>
                                          {' '}
                                          (
                                          <Box
                                            component="span"
                                            sx={{
                                              color: 'warning.main',
                                              fontWeight: 600,
                                            }}
                                          >
                                            {comboCustomizedCount} customized
                                          </Box>
                                          )
                                        </>
                                      )}
                                    </Typography>
                                    {editingComboId !== d.combinationId && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        disabled={recomputing}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setEditingComboId(d.combinationId)
                                          setEditingGrid(
                                            grid.map((item: any) => ({
                                              ...item,
                                            })),
                                          )
                                          setEditingDeliveryItems(
                                            (d.deliveryItems as string[]) || [],
                                          )
                                          setExpandedComboId(d.combinationId)
                                        }}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: 11,
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    )}
                                  </Box>
                                  <Collapse in={isExpanded}>
                                    {(() => {
                                      const isEditing =
                                        editingComboId === d.combinationId
                                      const displayGrid =
                                        isEditing && editingGrid
                                          ? editingGrid
                                          : grid
                                      const displayItems =
                                        isEditing && editingDeliveryItems
                                          ? new Set(editingDeliveryItems)
                                          : new Set(
                                              (d.deliveryItems as string[]) ||
                                                [],
                                            )
                                      if (displayGrid.length === 0) return null
                                      return (
                                        <Box sx={{ mt: 0.5 }}>
                                          {/* Edit controls: Save/Cancel + safe item chips */}
                                          {isEditing && (
                                            <Box sx={{ mb: 1 }}>
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  gap: 1,
                                                  mb: 1,
                                                }}
                                              >
                                                <Button
                                                  size="small"
                                                  variant="contained"
                                                  startIcon={
                                                    savingGrid ? (
                                                      <CircularProgress
                                                        size={12}
                                                      />
                                                    ) : (
                                                      <SaveIcon />
                                                    )
                                                  }
                                                  disabled={savingGrid}
                                                  onClick={handleSaveComboGrid}
                                                  sx={{
                                                    textTransform: 'none',
                                                    fontSize: 11,
                                                  }}
                                                >
                                                  Save
                                                </Button>
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  onClick={() => {
                                                    setEditingComboId(null)
                                                    setEditingGrid(null)
                                                    setEditingDeliveryItems(
                                                      null,
                                                    )
                                                  }}
                                                  sx={{
                                                    textTransform: 'none',
                                                    fontSize: 11,
                                                  }}
                                                >
                                                  Cancel
                                                </Button>
                                              </Box>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                  mb: 0.5,
                                                  display: 'block',
                                                }}
                                              >
                                                Delivery Safe Items (stay ON
                                                during delivery days):
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  flexWrap: 'wrap',
                                                  gap: 0.5,
                                                  mb: 1,
                                                }}
                                              >
                                                {allPrepItems.map((pi) => {
                                                  const isSafe =
                                                    displayItems.has(pi.id)
                                                  return (
                                                    <Chip
                                                      key={pi.id}
                                                      label={pi.name}
                                                      size="small"
                                                      color={
                                                        isSafe
                                                          ? 'info'
                                                          : 'default'
                                                      }
                                                      variant={
                                                        isSafe
                                                          ? 'filled'
                                                          : 'outlined'
                                                      }
                                                      clickable
                                                      onClick={() =>
                                                        toggleComboSafeItem(
                                                          pi.id,
                                                        )
                                                      }
                                                      sx={{
                                                        height: 22,
                                                        fontSize: 10,
                                                      }}
                                                    />
                                                  )
                                                })}
                                              </Box>
                                            </Box>
                                          )}
                                          <Box
                                            sx={{
                                              border: '1px solid #e0e0e0',
                                              borderRadius: 1,
                                              overflow: 'auto',
                                            }}
                                          >
                                            <table
                                              style={{
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                fontSize: 12,
                                                minWidth: 550,
                                              }}
                                            >
                                              <thead>
                                                <tr
                                                  style={{
                                                    backgroundColor: '#f5f5f5',
                                                  }}
                                                >
                                                  <th
                                                    style={{
                                                      textAlign: 'left',
                                                      padding: '5px 10px',
                                                      borderBottom:
                                                        '2px solid #e0e0e0',
                                                      minWidth: 130,
                                                    }}
                                                  >
                                                    Prep Item
                                                  </th>
                                                  {DAY_KEYS.map((day, i) => {
                                                    const dayNum =
                                                      DAY_KEY_TO_NUM[day]
                                                    const isDelivery =
                                                      deliveryDays.has(dayNum)
                                                    return (
                                                      <th
                                                        key={day}
                                                        style={{
                                                          textAlign: 'center',
                                                          padding: '5px 3px',
                                                          borderBottom:
                                                            '2px solid #e0e0e0',
                                                          width: 50,
                                                          backgroundColor:
                                                            isDelivery
                                                              ? '#e3f2fd'
                                                              : '#f5f5f5',
                                                        }}
                                                      >
                                                        {DAY_HEADERS[i]}
                                                      </th>
                                                    )
                                                  })}
                                                  <th
                                                    style={{
                                                      textAlign: 'center',
                                                      padding: '5px 3px',
                                                      borderBottom:
                                                        '2px solid #e0e0e0',
                                                      width: 40,
                                                    }}
                                                  >
                                                    Days
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {displayGrid.map(
                                                  (item: any) => {
                                                    const prepDays =
                                                      DAY_KEYS.filter(
                                                        (dk) => item[dk],
                                                      ).length
                                                    const isZero =
                                                      prepDays === 0
                                                    return (
                                                      <tr
                                                        key={item.prep_item_id}
                                                        style={{
                                                          backgroundColor:
                                                            isZero
                                                              ? '#ffebee'
                                                              : 'transparent',
                                                        }}
                                                      >
                                                        <td
                                                          style={{
                                                            padding: '2px 10px',
                                                            borderBottom:
                                                              '1px solid #eee',
                                                            fontSize: 12,
                                                            color: isZero
                                                              ? '#d32f2f'
                                                              : 'inherit',
                                                          }}
                                                        >
                                                          {item.prep_item_name}
                                                          {item.is_safe && (
                                                            <Chip
                                                              label="safe"
                                                              size="small"
                                                              color="info"
                                                              variant="outlined"
                                                              sx={{
                                                                ml: 0.5,
                                                                height: 16,
                                                                fontSize: 9,
                                                                '& .MuiChip-label':
                                                                  {
                                                                    px: 0.5,
                                                                  },
                                                              }}
                                                            />
                                                          )}
                                                        </td>
                                                        {DAY_KEYS.map((dk) => {
                                                          const dayNum =
                                                            DAY_KEY_TO_NUM[dk]
                                                          const isDelivery =
                                                            deliveryDays.has(
                                                              dayNum,
                                                            )
                                                          const isConstrained =
                                                            !item.is_safe &&
                                                            isDelivery
                                                          return (
                                                            <td
                                                              key={dk}
                                                              style={{
                                                                textAlign:
                                                                  'center',
                                                                padding:
                                                                  '1px 3px',
                                                                borderBottom:
                                                                  '1px solid #eee',
                                                                backgroundColor:
                                                                  isDelivery
                                                                    ? isZero
                                                                      ? '#ffcdd2'
                                                                      : '#e3f2fd'
                                                                    : isZero
                                                                    ? '#ffebee'
                                                                    : 'transparent',
                                                              }}
                                                            >
                                                              <Checkbox
                                                                size="small"
                                                                checked={
                                                                  !!item[dk]
                                                                }
                                                                disabled={
                                                                  !isEditing ||
                                                                  isConstrained
                                                                }
                                                                onChange={() => {
                                                                  if (
                                                                    !isEditing ||
                                                                    isConstrained
                                                                  )
                                                                    return
                                                                  toggleComboGridDay(
                                                                    item.prep_item_id,
                                                                    dk,
                                                                  )
                                                                }}
                                                                sx={{
                                                                  p: 0.15,
                                                                  '& .MuiSvgIcon-root':
                                                                    {
                                                                      fontSize: 15,
                                                                    },
                                                                }}
                                                              />
                                                            </td>
                                                          )
                                                        })}
                                                        <td
                                                          style={{
                                                            textAlign: 'center',
                                                            padding: '2px 3px',
                                                            borderBottom:
                                                              '1px solid #eee',
                                                            fontWeight: 600,
                                                            fontSize: 12,
                                                            color: isZero
                                                              ? '#d32f2f'
                                                              : 'inherit',
                                                          }}
                                                        >
                                                          {prepDays}
                                                        </td>
                                                      </tr>
                                                    )
                                                  },
                                                )}
                                              </tbody>
                                            </table>
                                          </Box>
                                        </Box>
                                      )
                                    })()}
                                  </Collapse>
                                </Box>
                              )
                            })}
                        </Box>
                      </Box>
                    )}

                  {/* Add Combination */}
                  {selectedScheduleId && (
                    <Box sx={{ mt: 2 }}>
                      {!addingCombo ? (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={recomputing}
                          onClick={() => setAddingCombo(true)}
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          + Add Combination
                        </Button>
                      ) : (
                        <Box
                          sx={{
                            p: 1.5,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 1, display: 'block' }}
                          >
                            Select delivery days for the new combination:
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              mb: 1.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            {DAY_HEADERS.map((label, i) => {
                              const dayNum = [1, 2, 3, 4, 5, 6, 0][i]
                              const isSelected = newComboDays.has(dayNum)
                              return (
                                <Chip
                                  key={dayNum}
                                  label={label}
                                  size="small"
                                  color={isSelected ? 'primary' : 'default'}
                                  variant={isSelected ? 'filled' : 'outlined'}
                                  clickable
                                  onClick={() => {
                                    setNewComboDays((prev) => {
                                      const next = new Set(prev)
                                      if (next.has(dayNum)) next.delete(dayNum)
                                      else next.add(dayNum)
                                      return next
                                    })
                                  }}
                                  sx={{ fontSize: 11 }}
                                />
                              )
                            })}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={
                                newComboDays.size === 0 || addingComboLoading
                              }
                              onClick={handleAddCombination}
                              sx={{ textTransform: 'none', fontSize: 11 }}
                            >
                              {addingComboLoading ? (
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                              ) : null}
                              Create
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setAddingCombo(false)
                                setNewComboDays(new Set())
                              }}
                              sx={{ textTransform: 'none', fontSize: 11 }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ScheduleDashboard
